import type { Context, Span } from '@opentelemetry/api';
import { logs, SeverityNumber } from '@opentelemetry/api-logs';
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { BatchLogRecordProcessor, LoggerProvider } from '@opentelemetry/sdk-logs';
import { BatchSpanProcessor, StackContextManager, WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { ATTR_SERVICE_NAME, ATTR_SERVICE_NAMESPACE, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions';

// Matches all Shiplex backend module paths: /voy/* (no /api suffix) and /<module>/api/*
const API_PATH_REGEX =
  /\/(voy|acc|att|bd|bun|comm|doc|exc|fre|frm|ism|map|md|oms|pda|pms|proc|pur|req|risk|rop|spo|tra|api)\//;

const LOGGER_SCOPE = 'Shiplex.Frontend';

let currentUserEmail: string | undefined;

/** Call after user signs in to attach their email to all subsequent spans. */
export function setTelemetryUser(email: string | undefined): void {
  currentUserEmail = email;
}

class UserEnrichmentProcessor {
  onStart(span: Span, _parentContext: Context): void {
    if (currentUserEmail !== undefined) {
      span.setAttribute('user.email', currentUserEmail);
    }
  }

  onEnd(): void {
    // no-op
  }

  shutdown(): Promise<void> {
    return Promise.resolve();
  }

  forceFlush(): Promise<void> {
    return Promise.resolve();
  }
}

function formatLogArg(arg: unknown): string {
  if (typeof arg === 'string') return arg;
  if (arg instanceof Error) return `${arg.name}: ${arg.message}\n${arg.stack ?? ''}`;
  try {
    return JSON.stringify(arg);
  } catch {
    return String(arg);
  }
}

let consolePatched = false;

function patchConsole(): void {
  if (consolePatched) return;
  consolePatched = true;

  const otelLogger = logs.getLogger(LOGGER_SCOPE);

  const originalWarn = console.warn.bind(console);
  const originalError = console.error.bind(console);

  // Guard against infinite recursion: OTEL's DiagConsoleLogger routes its own
  // diagnostics back through console.*, which would re-enter these patches.
  // Shared between warn and error — nested SDK diagnostics during forwarding are
  // intentionally dropped; preventing recursion takes priority.
  // Safe as long as otelLogger.emit() remains synchronous (true for current SDK).
  let isForwarding = false;

  console.warn = (...args: unknown[]) => {
    originalWarn(...args);
    if (isForwarding) return;
    isForwarding = true;
    try {
      otelLogger.emit({
        severityNumber: SeverityNumber.WARN,
        severityText: 'WARN',
        body: args.map(formatLogArg).join(' ')
      });
    } catch (err) {
      originalWarn('[telemetry] failed to forward to OTEL', err);
    } finally {
      isForwarding = false;
    }
  };

  console.error = (...args: unknown[]) => {
    originalError(...args);
    if (isForwarding) return;
    isForwarding = true;
    try {
      otelLogger.emit({
        severityNumber: SeverityNumber.ERROR,
        severityText: 'ERROR',
        body: args.map(formatLogArg).join(' ')
      });
    } catch (err) {
      originalWarn('[telemetry] failed to forward to OTEL', err);
    } finally {
      isForwarding = false;
    }
  };
}

export function initTelemetry(): void {
  const endpoint = import.meta.env.VITE_OTEL_ENDPOINT;
  if (!endpoint) return;

  const version = (import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'dev';

  const resource = resourceFromAttributes({
    [ATTR_SERVICE_NAME]: 'Shiplex.Frontend',
    [ATTR_SERVICE_NAMESPACE]: 'Shiplex',
    [ATTR_SERVICE_VERSION]: version
  });

  const loggerProvider = new LoggerProvider({
    resource,
    processors: [new BatchLogRecordProcessor(new OTLPLogExporter({ url: `${endpoint}/v1/logs` }))]
  });
  logs.setGlobalLoggerProvider(loggerProvider);

  const provider = new WebTracerProvider({
    resource,
    spanProcessors: [
      new UserEnrichmentProcessor(),
      new BatchSpanProcessor(new OTLPTraceExporter({ url: `${endpoint}/v1/traces` }))
    ]
  });

  provider.register({ contextManager: new StackContextManager() });

  registerInstrumentations({
    instrumentations: [
      new DocumentLoadInstrumentation(),
      new XMLHttpRequestInstrumentation({ propagateTraceHeaderCorsUrls: [API_PATH_REGEX] }),
      new FetchInstrumentation({ propagateTraceHeaderCorsUrls: [API_PATH_REGEX] }),
      new UserInteractionInstrumentation()
    ]
  });

  patchConsole();
}
